// PageDescription: FAQ 상세 페이지
import { dehydrate } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { NextSeo } from 'next-seo';
import Head from 'next/head';
import React from 'react';

import DefaultLayout from '@/components/layouts/DefaultLayout';
import FaqDetailTemplate from '@/components/templates/communication/faq/faq-detail-template';
import { SIX_HOURS_IN_MILLISECONDS } from '@/constants/common';
import useGetBannerAdExchange from '@/hooks/advertisement/useGetBannerAdExchange';
import useUnifiedAdBanner from '@/hooks/advertisement/useUnifiedAdBanner';
import useAppInstallTopBanner from '@/hooks/cuj/useAppInstallTopBanner';
import useFaqDetailViewModel from '@/hooks/faq/useFaqDetailViewModel';
import useGetRelatedFaq from '@/hooks/faq/useGetRelatedFaq';
import useGetMagazineDetail from '@/hooks/magazine/useGetMagazineDetail';
import useGetPrevNextMagazinePid from '@/hooks/magazine/useGetPrevNextMagazinePid';
import useGetMagazineRelatedQna from '@/hooks/qna/useGetMagazineRelatedQna';
import { AdPageType } from '@/models/ad/list';
import { ExtendedQueryClient } from '@/models/common/query-client';
import { ssrQueryCache } from '@/models/common/ssr-cache';
import { formatAuthorType } from '@/shared/utils/format';
import { CustomPageProps } from '@/types/app';
import { ROUTES } from '@/types/common';
import { GraphqlError, ServerError } from '@/types/error';
import { MagazineDetailVo, MagazineType } from '@/types/magazine';
import { convertMarkdownToPlainText } from '@/utils/format';
import { withClientServerSideProps } from '@/utils/mobile/RNPlugins';
import dayjs from 'dayjs';

export const createFaqLdJson = (faq?: MagazineDetailVo) => {
  if (!faq) {
    return;
  }

  const author = {
    '@type': 'Person',
    name: faq.author.name,
    ...(faq.author.profileDescription && {
      description: faq.author.profileDescription,
    }),
    jobTitle: formatAuthorType(faq.author.authorType),
    url: `${process.env.NEXT_PUBLIC_DOMAIN}/author/${faq.author.authorPublicId}`,
    image: faq.author.profileImageUrl,
    affiliation: {
      '@type': 'Organization',
      name: '닥터나우',
      url: `${process.env.NEXT_PUBLIC_DOMAIN}`,
    },
    alumniOf: faq.author.careers.map((career) => ({
      '@type': 'Organization',
      name: career.content,
    })),
  };

  const questionSchema = {
    '@type': 'Question',
    '@id': `${process.env.NEXT_PUBLIC_DOMAIN}/content/faq/${faq.magazinePid}#question`,
    name: faq.title,
    text: faq.description,
    dateCreated: dayjs(faq.createdAt).format('YYYY-MM-DDTHH:mm:ss[Z]'),
    answerCount: 1,
    upvoteCount: faq.likeCount,
    author,

    acceptedAnswer: {
      '@type': 'Answer',
      text: convertMarkdownToPlainText(faq.content),
      dateCreated: dayjs(faq.createdAt).format('YYYY-MM-DDTHH:mm:ss[Z]'),
      upvoteCount: faq.likeCount,
      url: `${process.env.NEXT_PUBLIC_DOMAIN}/content/faq/${faq.magazinePid}`,
      author: author,
    },
  };

  return [
    {
      '@context': 'https://schema.org',
      '@type': 'QAPage',
      '@id': `${process.env.NEXT_PUBLIC_DOMAIN}/content/faq/${faq.magazinePid}`,
      mainEntity: questionSchema,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: faq.title,
      description: faq.description,
      url: `${process.env.NEXT_PUBLIC_DOMAIN}/content/faq/${faq.magazinePid}`,
      author,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '닥터나우',
      url: `${process.env.NEXT_PUBLIC_DOMAIN}`,
      logo: 'https://media.doctornow.co.kr/og-main.png',
      contactPoint: [
        {
          '@type': 'ContactPoint',
          email: 'contact@doctornow.co.kr',
          contactType: 'customer service',
        },
      ],
    },
  ];
};

interface Props extends CustomPageProps {
  id: string;
}

const FaqDetail = ({ id, client }: Props) => {
  const viewModel = useFaqDetailViewModel({ faqPid: id, client });
  const topBannerViewModel = useAppInstallTopBanner();
  const ldJson = createFaqLdJson(viewModel.faq);

  return (
    <>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(ldJson ?? {}) }} />
      </Head>
      <NextSeo
        title={`${viewModel.faq?.title} - 닥터나우`}
        canonical={`${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.FAQ.DETAIL.path}/${id}`}
        description={viewModel.faq?.description}
        openGraph={{
          url: `${process.env.NEXT_PUBLIC_DOMAIN}${ROUTES.FAQ.DETAIL.path}/${id}`,
          title: viewModel.faq?.title || '',
          description: viewModel.faq?.description || '',
          images: [
            {
              url: `${process.env.NEXT_PUBLIC_MAGAZINE_OG_IMAGE}`,
              alt: 'Og Image Alt',
            },
          ],
        }}
      />
      <FaqDetailTemplate viewModel={viewModel} topBannerViewModel={topBannerViewModel} client={client} />
    </>
  );
};

FaqDetail.Layout = DefaultLayout;

export default FaqDetail;

export const getServerSideProps: GetServerSideProps = withClientServerSideProps(async (ctx) => {
  const id = ctx.params?.id as string;
  const client = ctx.client;

  if (!id) {
    return {
      redirect: {
        destination: ROUTES.FAQ.LIST.path,
        permanent: true,
      },
    };
  }

  const queryClient = new ExtendedQueryClient();

  const cacheKey = ctx.req.url as string;

  try {
    if (ssrQueryCache.has(cacheKey)) {
      queryClient.setQueryDataFromCache(ssrQueryCache.get(cacheKey) ?? []);
      await useUnifiedAdBanner.prefetchQuery(queryClient, AdPageType.TopCommonBanner, client);

      return {
        props: {
          id,
          dehydratedState: JSON.parse(JSON.stringify(dehydrate(queryClient))),
        },
      };
    }

    await Promise.allSettled([
      useGetBannerAdExchange.prefetchQuery(queryClient, AdPageType.TopCtaBand, client),
      useUnifiedAdBanner.prefetchQuery(queryClient, AdPageType.TopCommonBanner, client),
      useGetMagazineDetail.prefetchQuery(queryClient, id),
      useGetPrevNextMagazinePid.prefetchQuery(queryClient, {
        magazinePid: id,
        contentType: MagazineType.FAQ,
      }),
      useGetRelatedFaq.prefetchQuery(queryClient, id),
      useGetMagazineRelatedQna.prefetchQuery(queryClient, id),
    ]);

    ssrQueryCache.set(
      cacheKey,
      queryClient.getQueryClientData([
        useGetMagazineDetail.getKey(id),
        useGetPrevNextMagazinePid.getKey({ magazinePid: id, contentType: MagazineType.FAQ }),
        useGetRelatedFaq.getKey(id),
        useGetMagazineRelatedQna.getKey(id),
      ]),
      SIX_HOURS_IN_MILLISECONDS
    );

    return {
      props: {
        id,
        dehydratedState: dehydrate(queryClient),
      },
    };
  } catch (e) {
    const { code } = e as unknown as GraphqlError;
    if (code === ServerError.NotFound) {
      return {
        notFound: true,
      };
    }
    if (ctx.res && code === ServerError.BadRequest) {
      ctx.res.statusCode = 400;
    }

    return {
      notFound: true,
    };
  }
});
